import React from "react";

const IndexPage = ({ lang }) => (
  <div className="container">
    <h2 align="center">
      <b>DeloitteDirect</b>
    </h2>
    <h3 align="center">
      <b>Privacy Notice</b>
    </h3>
    <br />
    <p align="right">LAST REVISED: 25 MAY 2018</p>
    <p>
      <b>Why this Privacy Notice </b>
    </p>
    <p>
      Your privacy is important to us. We want you to be informed and familiar
      with how we collect, use and disclose personal data relating to the
      services we provide you through the
      <b>DeloitteDirect Portal (“the Portal”).</b>
    </p>
    <p>
      This Privacy Notice (also <b>“Notice”</b>) describes how Deloitte (or
      <b>"we"</b>) processes personal data collected in relation to the Portal.
    </p>
    <p>
      You will find below information about the types of personal data we
      collect from you when making use of the Portal and how we process your
      personal data, which parties we share your personal data with, the
      duration for keeping them and your rights regarding our use of your
      personal data.
    </p>
    <p>We also describe how you can contact us about our privacy practices.</p>
    <p>We thank you for reading this Privacy Notice carefully.</p>
    <p>
      By using the Portal, you have read this Privacy Notice. If for any reason
      you have concerns or enquiries about how we process your personal data,
      please contact your Deloitte contact.
    </p>
    <br />
    <p>
      <b>Collecting your personal data</b>
    </p>
    <p>
      While providing the agreed Services to you, we are responsible for looking
      after the personal data you entrust us with. For clarity, please note
      that:
    </p>
    <p>
      <b>
        <i>Personal data</i>
      </b>
      is any information that directly identifies an individual, such as a name,
      address, date of birth, email address, IP address, telephone number,
      marital status, income and financial statements. It also includes
      information that can indirectly identify a person, or that can potentially
      identify an individual if it is combined with other information. For
      example, an account number is not personal data in isolation but it
      becomes personal data when it is associated with the name of the account
      holder.
    </p>
    <p>
      <b>
        <i>Processing personal data</i>
      </b>
      refers to anything we do with your personal data. This includes the
      collection, recording, storage, use, consultation, alteration, disclosure,
      dissemination, restriction or destruction of personal data.
    </p>
    <br />
    <p>
      <b>Types of data we process</b>
    </p>
    <p>
      We commit to collecting only personal data that are necessary to perform
      the engagement and the obligations we undertake towards you through the
      Portal; in a manner that only data that are adequate and relevant to the
      service are processed.
    </p>
    <p>
      Typically, we process one or more of the following types of personal data
      through the Portal depending on the services you have asked us to provide
      through the Portal’s applications or because of the on-line usage of the
      Portal:
    </p>
    <ul>
      <li>
        <p>
          Professional contact details, such as professional title and function,
          work telephone number and email address.
        </p>
      </li>
      <li>
        <p>
          Personal identifying information, notably first/last name, date of
          birth, personal email and telephone number, home address.
        </p>
      </li>
      <li>
        <p>
          Human resources/payroll data, notably function, seniority and ranking,
          cost center allocation, compensation details, benefits, working time
          (absence, leave, etc.), group insurance and pension details, family
          related information.
        </p>
      </li>
      <li>
        <p>
          Financial data including bank account statements, credit or debit card
          numbers, revenues, savings, investments, investment expenses, revenues
          evaluation, allowances and subsidies, financial transactions.
        </p>
      </li>
      <li>
        <p>
          Information related to commercial agreements, type and nature of
          professional activities, details related to agents, licenses.
        </p>
      </li>
      <li>
        <p>
          IP addresses or other device identifiers, electronic communications
          metadata (time and duration of connection with Portal), authorization
          credentials (user name/passwords).
        </p>
      </li>
      <li>
        <p>
          Details related to questions, requests and complaints you asked us to
          deal with through the use of the Portal.
        </p>
      </li>
    </ul>
    <p>
      The above types of data mainly concern you as authorized user of the
      Portal, as well as the members of staff of your organization or other
      beneficiary (e.g. family members), your clients, suppliers and business
      partners.
    </p>
    <br />
    <p>
      <b>Purposes of data processing</b>
    </p>
    <p>
      The general rule is that we will process your personal data only for the
      specific and defined purposes described in our with you and according to
      your instructions. We may however use them for our legitimate business
      purposes as well, such as to protect the security of your transactions
      over the Portal. We do not process your personal data for purposes which
      do not relate to products, applications and services enabled through the
      Portal or your enquiries; or which go beyond what you could reasonably
      expect us to do with those data as data processors.
    </p>
    <p>
      Accordingly, we process personal data on the Portal for the following
      purposes: 
    </p>
    <ul>
      <li>
        <p>
          <b>Customer file administration</b>: to grant you access to the
          Portal, we need to process for example information relating to your
          identity, profession and financial status, to proceed to required
          conflicts’ and independence checking or to generate and keep safe
          information related to your registration on the Portal;
        </p>
      </li>
      <li>
        <p>
          <b>Delivery of services</b>: Depending on the services you opted for
          on the Portal through the specific applications we offer you, we have
          to consolidate, centrally store, structure or transmit information
          that may contain personal data. Amongst the services supported through
          the Portal are for example the generation of financial analysis
          reporting, document storage and archiving, document scanning,
          administration of bookkeeping and payroll entries (for posting into
          the selected accounting or HR/payroll software), facilitation with
          proposal generation up to invoicing, overall back-office support
          relating to document management and reporting.
        </p>
      </li>
      <li>
        <p>
          <b>Granting access to applications</b>: for instance, we need to
          generate and process authentication credentials for the persons
          authorized to access the Portal on your behalf, to activate them on
          the applications you have opted for or give you support for managing
          those authorizations on your own.
        </p>
      </li>
      <li>
        <p>
          <b>Back-office Portal administration</b>: although we do not have the
          intention to, we may process personal data while we are conducting
          applications’ upgrading, testing including with regard to the
          deployment of new applications, software maintenance, troubleshooting
          management and quality control reviews relating to the Portal’s smooth
          functioning or while we look into ways to optimize the functionalities
          of the Portal.
        </p>
      </li>
      <li>
        <p>
          <b>Optimize Portal’s use</b>: to make our interactions with you more
          efficient, and to respond timely to your questions through the Portal,
          a restricted number of Deloitte practitioners get an overview of
          actions made by you and the Deloitte project team supporting you. Only
          a dedicated team of practitioners of Deloitte specifically authorized
          for this purpose may have access to this overview through a dedicated
          application of the Portal.
        </p>
      </li>
      <li>
        <p>
          <b>Customer Relationship Management (CRM)</b>: We may use the data fed
          in the Portal with a view to better understand your needs, take
          contact with you and, eventually, propose you more customized or
          additional services. In particular, in order to respond more
          efficiently to your requests and needs, we centralize on a unique
          place of the Portal all the data you communicate to us through the
          other services of the Portal. For example, the Portal sends us
          real-time alerts whenever your business situation changes (e.g., you
          update your contact details). We also use this central data repository
          to manage better our communication and marketing campaigns towards
          groups of clients (e.g., by sending you commercial communications
          relevant to your type of business or industry sector). In these cases,
          Deloitte undertakes to comply with all requirements defined under the
          applicable law, including getting your consent if required, before
          engaging in those activities.
        </p>
      </li>
      <li>
        <p>
          <b>Data security</b>: it is our obligation and legitimate interest to
          ensure that your data on the Portal are protected against unauthorized
          or accidental access, damage or other inappropriate use. For this
          reason, we may need to process file logs, IP addresses,
          geo-localization or other metadata when this is necessary to preserve
          our company’s assets, the confidentiality of your information or to
          respond to a security incident.
        </p>
      </li>
      <li>
        <p>
          <b>Anonymized user analytics and industry benchmarking</b>: to measure
          the Portal’s effectiveness, its usage and in order to plan future
          improvements, we will potentially use data analysis across Deloitte
          member firms. Data analysis may also be used to reconcile customers’
          trends and perform benchmarking per industry, business sector, type of
          location or other parameters. Note that only aggregate and/or
          anonymized data will be processed in this case in compliance with
          applicable law.
        </p>
      </li>
    </ul>
    <br />
    <b>Disclosing your personal data</b>
    <p>
      To provide you with the breadth of services available on the Portal, we
      have to call upon third-party suppliers of IT infrastructure, software
      tools and other IT service components and applications. In all these
      cases, we strictly instruct our third party service providers by a written
      contract (likewise the sub-processors they may hire) to not use your
      personal data for other purpose than those we have defined in the services
      agreement. We impose on them strict obligations relating to data
      confidentiality, security, access management, restricted use of data and
      data return or deletion according to the requirements of the applicable
      law and the contract we have with you.
    </p>
    <p>
      The types of services for which we use third-party service providers
      (“sub-processors”) are:
    </p>
    <ul>
      <li>
        <p>
          Operation of the Portal; development, maintenance and deployment of
          the Portal in our company;
        </p>
      </li>
      <li>
        <p>
          Trouble-shooting, provision of support services incl. quality
          reporting in relation to the Portal.
        </p>
      </li>
      <li>
        <p>Hosting of applications and of data (data storage);</p>
      </li>
      <li>
        <p>
          Monitoring and administration of IT infrastructure (Portal’s servers)
        </p>
      </li>
      <li>
        <p>
          Developing and maintaining software depending on the services you opt
          for (e.g. we cooperate with software providers of bookkeeping
          packages)
        </p>
      </li>
      <li>
        <p>
          Applications’ vendors or developers for connection/interaction with
          the Portal, adding or upgrading Portal’s functionalities.
        </p>
      </li>
    </ul>
    <p>
      If you have questions or wish to receive more information or the full list
      of the specific providers we use in connection with the Portal in
      Deloitte, please contact your Deloitte contact.
    </p>
    <p>
      When working with sub-contractors outside of the country of your Deloitte
      relationship, we take appropriate measures to ensure that personal data
      will be adequately protected in the recipient country. In particular, we
      make sure that personal data is never transferred outside of the European
      Economic Area (EEA) and the country of your location.
    </p>
    <p>
      Note that the sub-contractors which support us in connection with the
      Portal are bound by equivalent obligations and commitments as those
      described in this Notice.
    </p>
    <br />
    <p>
      <b>Exercising your rights</b>
    </p>
    <p>
      As a provider of services to you, we are committed to assist you in
      responding efficiently to the requests you may receive from your data
      subjects (employees, associates, representatives, your clients, etc.)
      relevant to the exercise of their rights relating to data processing
      through the Portal. You may need our help to:
    </p>
    <ul>
      <li>
        <p>
          Confirm whether we process personal data you are responsible for as
          data controller, to get access to or help you receive a copy of the
          personal data requested;
        </p>
      </li>
      <li>
        <p>
          Respond to your requests to rectify or update any inaccurate personal
          data, or complete any incomplete personal data to the extent that
          those updates depend on actions we have to take on the Portal;
        </p>
      </li>
      <li>
        <p>To satisfy personal data deletion/erasure requests;</p>
      </li>
      <li>
        <p>To restrict our processing of personal data; and</p>
      </li>
      <li>
        <p>
          To require us to transmit personal data to you or to transfer or have
          them transferred to another data controller (portability);
        </p>
      </li>
    </ul>
    <p>
      Concurrently, we will respond to your requests of exercising the above
      rights relevant to the personal data we collect, process and store about
      you through the Portal and the applications connected to it.
    </p>
    <p>
      You can also object to our using your personal data for some of the
      purposes described herein (see Purposes of data processing) if you have
      legitimate personal reasons for asking us to stop using them. We will then
      have to stop the processing unless we must continue using your data for
      other reasons despite your objection (e.g., legal claim or contractual
      obligation). To note however that if you are opposed to certain data
      processing through this Portal, we may not be able to offer you full
      access to all the Portal’s functionalities you subscribed to.
    </p>
    <p>
      Any requests related to the data subject’s rights above can be
      communicated to your Deloitte contact.
    </p>
    <br />
    <p>
      <b>Storing your personal data</b>
    </p>
    <p>
      We are committed to keeping your data accurate and up-to-date. We do not
      have any intention of storing your data for longer than it is necessary
      for providing you with the Portal’s services you subscribed to.
    </p>
    <p>
      How long we need to keep your data depends on the purpose for which we
      collect them but also specific requirements laid down in our contract with
      you. Therefore, there is no universal retention period applying to all
      types of personal data stored on the Portal.
    </p>
    <p>
      We also need to consider other reasons and therefore we might need to keep
      personal data for longer than necessary for your use of the Portal.
      Relevant examples are obligations to keep data as legal evidence in legal
      proceedings or specific data retention requirements stemming from
      sector-specific laws (in audit, tax, etc.).
    </p>
    <p>
      As a general rule and to the extent that no exception applicable on us
      (see above) would require that we keep your data for a longer time period,
      we commit to delete or return to you the personal data and all relevant
      copies thereof at the latest by the end of the 12th month after the date
      of term or termination of our contract with you.
    </p>
    <p>
      Should you wish to obtain more information on where and how long your
      personal data is stored, and on your right of erasure, please contact your
      Deloitte contact.
    </p>
    <br />
    <p>
      <b>Protecting your personal data</b>
    </p>
    <p>
      According to the applicable law, we take appropriate technical and
      organizational measures to ensure that the personal data processed in
      relation to the operations described herein are adequately protected
      against accidental or unauthorized disclosure or access, destruction or
      other unlawful forms of processing.
    </p>
    <p>
      These measures include indicatively: data access management procedures;
      secure data transmission (e.g. https/SSL/TLS 1.2 standards) over the
      network; periodical penetration testing and cyber risk reviews of the
      Portal; logging; secure archiving and back-ups and other measures
      according to industry best practices and widely acknowledged certification
      schemes (e.g. ISO 27001).
    </p>
    <br />
    <p>
      <b>Questions, concerns and complaints</b>
    </p>
    <p>
      Should you have any question or concern on how Deloitte process your
      personal data in relation to the Portal, you can contact your Deloitte
      contact.
    </p>
    <p>
      You also have a right to lodge a complaint with the relevant supervisory
      authority (in particular in the Member State of your habitual residence,
      place of work or place of the alleged infringement).
    </p>
    <br />
    <p>
      <b>Updates to this Privacy Notice</b>
    </p>
    <p>
      You can find out when this Privacy Notice was last amended by checking
      "LAST REVISED" at the top of this Notice.
    </p>
    <br />
    <p>
      <b>
        To download this Privacy Notice, please click&nbsp;
        <a
          href={`DeloitteDirect.Privacy.Statement_${lang}.pdf`}
          title="DeloitteDirect.Privacy.Statement"
        >
          <span>
            <b>Deloitte Direct privacy statement</b>
          </span>
        </a>
      </b>
      .
    </p>
  </div>
);

export default IndexPage;
